export interface ReleaseNote {
  version: string;
  date: string;
  title?: string;
  changes: string[];
}

export const releaseNotes: ReleaseNote[] = [
  // ═══════════════════════════════════════════════════════════════
  // 📋 TEMPLATE FOR NEW RELEASES:
  // ═══════════════════════════════════════════════════════════════
  // {
  //   version: 'v1.2.0',
  //   date: 'January 15, 2025',
  //   title: 'Your Release Title',
  //   changes: [
  //     'Feature description',
  //     'Bug fix description',
  //     'Another improvement',
  //   ],
  // },

  // ═══════════════════════════════════════════════════════════════
  // 🎨 STYLING NOTES:
  // ═══════════════════════════════════════════════════════════════
  // Version Chip Colors (auto-detected by comparing to previous version):
  // - 🟡 Gold: Major version bump (1.x.x → 2.0.0)
  // - 🟢 Green: Minor version bump (1.8.x → 1.9.0)
  // - 🔵 Blue: Patch version bump (1.8.0 → 1.8.1)
  //
  // Change Icons (automatically determined by text content):
  // - 🟢 Green Star: Changes containing "new" or "added"
  // - 🔵 Blue Release: All other changes (default)
  // - 🟠 Orange Bug: Changes containing "bug fix", "fixed", "resolve", or "invalid"
  //
  // {
  //   version: 'v1.1.1',
  //   date: 'Sep 14, 2025',
  //   title: 'Small update',
  //   changes: ['tbh nothing really', 'bug fixes: removed Grand Mother Silk from the Citadel', 'added cats'],
  // },

  {
    version: 'v2.2.0',
    date: 'October 9, 2026',
    title: 'One Tenant, Multiple Units',
    changes: [
      "New: add the same tenant to multiple units using one email address. Owners and property managers can now receive every unit's invoices in one inbox, with no need for a separate email per unit. Each unit keeps its own lease dates, invoice contact, and alert settings.",
      'New: adding an email that already belongs to a tenant now shows who they are and asks whether to add them to the new unit or move them. Previously the tenant was moved without warning.',
      'New: remove a tenant from a single unit without deleting them. Open the row menu on the Tenants page and choose "Remove from unit"; they stay in their other units.',
      'Spreadsheet imports now accept the same email on rows for different units, with a confirmation step before a tenant is placed in multiple units. Imports only add and update tenants; they never remove anyone.',
      'Tenants in more than one unit show a "+N units" tag on each of their rows, so shared tenants are easy to spot at a glance.',
      'Deleting a tenant now spells out that it removes them from every unit they occupy, on every property, and warns you when they have units on your other properties. To take someone out of just one unit, use "Remove from unit" instead.',
      'Fixed the home page showing a red error icon for a property while a newly installed meter was still completing its installation check. The property card now shows green and reads "Installation Check In Progress", matching what you see inside the unit.',
    ],
  },
  {
    version: 'v2.1.3',
    date: 'October 1, 2026',
    title: 'Leak Alert Follow-Through',
    changes: [
      'Fixed a gap in how we measure whether leak alert emails lead people back to the unit usage page, so we can keep improving when and how alerts are sent.',
    ],
  },
  {
    version: 'v2.1.2',
    date: 'September 25, 2026',
    title: 'Usage Insights Update',
    changes: [
      'Updated the tools we use to understand how the app is used, so we can spot rough edges and improve the experience faster.',
    ],
  },
  {
    version: 'v2.1.1',
    date: 'September 23, 2026',
    title: 'Saved Fees Improvements',
    changes: [
      'Improved the Saved Fees window with a cleaner look: a clearer title, a close button, and dividers between saved fees so long lists are easier to scan.',
    ],
  },
  {
    version: 'v2.1.0',
    date: 'September 9, 2026',
    title: 'Reusable Fees, Usage Breakdown & Tenant Bill History',
    changes: [
      'New: save a fee while adding it to a bill, then re-add it to future bills with one click. No more retyping the same sewer or trash fee every cycle.',
      'New: saved fees are kept per property. Saving a fee again with the same name updates its amount, and you can remove a fee from the saved list at any time.',
      'Unit-specific saved fees ask which units to apply to each time, so they always match your current tenant list.',
      'New: the property Usage tab now includes a Per Unit Breakdown chart, so you can see which units drive total usage at a glance. Click a unit in the legend to view it on its own. Large properties show the top seven units, with the rest grouped as "All other units".',
      'New: view any tenant\'s billing history straight from the Tenants page. Open the row menu and choose "View Bills" to see their invoices, due dates, and PDF downloads in one place, including past tenants.',
      'Fixed usage charts that could appear blank after switching months or flipping between daily and hourly views. Charts now draw immediately on every screen.',
      'Improved chart colors so every unit and meter stays distinguishable, including for colorblind users, and chart legends can now be operated with a keyboard.',
    ],
  },
  {
    version: 'v2.0.7',
    date: 'August 31, 2026',
    title: 'Export and Due Date Accuracy',
    changes: [
      'Fixed spreadsheet and PMS exports for units where only some tenant invoices have been generated: those units now show the full unit amount instead of a partial one.',
      'Fixed due dates in the bill breakdown so they always display in the property’s timezone, matching the date printed on the invoice.',
    ],
  },
  {
    version: 'v2.0.6',
    date: 'August 27, 2026',
    title: 'Billing Reliability',
    changes: [
      'Saving a bill without changing anything now leaves its generated invoices exactly as they were; invoices are only refreshed when the bill actually changes.',
      'Invoice emails go only to the tenant each invoice belongs to, and delivery retries can no longer produce duplicate emails. Re-sending invoices on purpose still works any time.',
      'A tenant who moves out and back in during the same billing period now receives a single invoice covering both stays.',
      'The unit Bill History page now shows the correct due date and download for SimpleSUB invoices.',
      'Improved handling of unusual meter readings during billing and in the bill breakdown.',
    ],
  },
  {
    version: 'v2.0.5',
    date: 'August 21, 2026',
    title: 'Invoice Preview Clarity & PMS Export Accuracy',
    changes: [
      'New: the bill review screen now shows which tenants will be invoiced, with their occupancy dates, before invoices are generated.',
      'New: when a tenant occupied their unit for part of the billing period, the review screen and the Generate Invoices dialog now explain up front that their invoice covers only that occupancy, so invoice totals will come out below the utility bill total.',
      'New: after invoices are generated, the Total Cost card now shows the total invoiced to tenants and, when tenants occupied their units for only part of the period, the share of the bill the owner absorbed for the uncovered days.',
      'Fixed ResMan exports leaving the Resident Name blank for tenants whose lease ends on the billing period’s final day.',
      'Fixed the Rent Manager upload confirmation occasionally listing tenants from just outside the billing period; tenant occupancy now follows the property’s timezone exactly.',
    ],
  },
  {
    version: 'v2.0.4',
    date: 'August 17, 2026',
    title: 'Billing Safeguards',
    changes: [
      'Bill generation now stops with a clear explanation when a unit records negative water usage for the billing period, which usually means a meter is installed in reverse. Contact SimpleSUB Water support to get the usage data corrected before billing.',
      'Billing errors now show the specific reason a bill could not be generated instead of a generic error message.',
    ],
  },
  {
    version: 'v2.0.3',
    date: 'August 14, 2026',
    title: 'Invoice Consistency',
    changes: [
      'The bill breakdown now displays each invoice’s line items exactly as they appear on the PDF, so on-screen amounts, estimated usage, and downloaded invoices always agree, including per-tenant details on prorated units.',
      'Faster and more reliable invoice downloads for very large properties.',
    ],
  },
  {
    version: 'v2.0.1',
    date: 'July 30, 2026',
    title: 'Occupancy Refinements',
    changes: [
      'The Move Out Date is now the tenant’s Last Day of Lease: tenants are billed through that day, matching how leases are written. Previously billing stopped the day before.',
      'Tenants can have a one-day tenancy (moving in and leaving on the same date).',
      'To prevent double billing, a tenant’s move-in can no longer land on the previous tenant’s last day; turnovers start the following day. The error message suggests the exact dates to use, and co-tenants sharing a unit are unaffected.',
      'Usage graph totals include estimated usage: the total shows the combined number with the metered and estimated amounts listed beneath it, on both the unit and property pages.',
      'Fixed usage reports and bills erroring or missing usage after a meter was moved between units; usage now always counts toward the unit where it was measured.',
      'Fixed invoice downloads occasionally failing for very large properties.',
      'Fixed the usage graph and totals on SSW invoice PDFs for units with zero or irregular usage.',
    ],
  },
  {
    version: 'v2.0.0',
    date: 'July 22, 2026',
    title: 'Tenant Occupancy Dates and Invoice Proration',
    changes: [
      'New Occupancy Dates and Invoice Contact columns on the Tenants tab, which drive the new prorated billing.',
      'New prorated invoicing: when tenants move in or out mid-cycle, each tenant gets their own invoice for just the days they occupied the unit. Covers both mid-cycle turnover and single-tenant partial occupancy.',
      'Setting a move-out date on the current invoice contact now auto-promotes the next eligible tenant as the invoice contact.',
      'New regen prompt on the bill page when tenant occupancy changes after invoices were generated, with a single-click Regenerate Invoices button. Catches new and removed tenants as well as same-tenant date edits that shift billable days.',
      'Prorated invoice PDFs now include an occupancy line ("Billed for occupancy: X to Y") so tenants see when their invoice covers only part of the period.',
      'Send Invoices by Email now skips invoices with no valid recipient and sends the rest, instead of blocking the whole send.',
      'Loading indicator on the Invoices Emailed column, so the count no longer briefly shows 0 of N while loading.',
      'Rent Manager Upload Charges now skips units with mid-cycle turnover, since Rent Manager cannot represent a prorated split across tenants.',
      'Tenants tab now defaults to sorting by Unit Name naturally (Unit 2 before Unit 10), with move-in date as the tiebreaker.',
      'Added estimated usage: for periods where meter data is unavailable, support-entered estimates now appear on usage charts, invoices, tenant usage reports, and spreadsheet exports. Estimates always show separately from metered usage (a gray striped segment on charts, with the reason on hover) and totals are labeled as metered + estimated.',
      'The Regenerate Invoices prompt now also fires when estimated usage or late-arriving meter data changes a bill after invoices were generated.',
      'Fixed sending tenant usage reports for accounts without the hourly usage feature.',
      'Fixed report send failures being silently ignored: errors now show a message instead of the dialog closing as if the send succeeded.',
      'Fixed a grey loading button that briefly appeared in the bill breakdown.',
      'Fixed confusing error messages that appeared when a Billing Manager opened the Usage or Alert History views. Because those views require usage access, they now show a clear permission message instead of an error.',
      'Fixed the confusing database error shown when deleting a unit or property that still has billing or alert history. It now shows a clear message explaining why it cannot be deleted, instead of a raw error.',
    ],
  },
  {
    version: 'v1.12.0',
    date: 'May 20, 2026',
    title: 'PMS Expansion (Beta)',
    changes: [
      'New AppFolio Bulk Charges export is now available in Beta. Contact Support to enable for your account; you can then upload AppFolio’s import template, map your SimpleSUB units to AppFolio occupancies, and download a ready-to-import CSV from any billing breakdown.',
      'New ResMan Transactions export is now available in Beta. Contact Support to enable for your account; you can then configure your property in PMS Settings, map units manually, and download a ResMan CSV from any billing breakdown.',
      'New fuzzy matching during Auto-Match handles small formatting differences in unit names (Apt 1 vs Apt-1 vs Apt #1) without merging genuinely distinct units like Apt 1 and Apt 11. A small Fuzzy chip flags any normalized match so you can double-check it before saving.',
      'Re-uploading your AppFolio template now preserves existing unit mappings via the AppFolio Unit ID, so renaming a unit in AppFolio won’t break your saved mappings.',
      'Fixed a bug where the Connect to Rent Manager option could appear in the bill breakdown PMS setup even when your account didn’t have the Rent Manager API integration enabled.',
    ],
  },
  {
    version: 'v1.11.4',
    date: 'May 13, 2026',
    title: 'You Asked, We Listened',
    changes: [
      'Added support for entering bills in additional volume units (Gallons, 100 Gallons, Liters, 1000 Liters, and CF) so you can match the unit shown on your utility bill',
      'New URL history picker in the Pay Online URL field on invoice generation dialogs lets you quickly re-select a URL used on a past invoice',
      'New info icon on the Invoice column of the bills table reveals which Pay Online URL and Comments were used on past invoices',
      'Added the unit name to custom invoice emails so tenants can tell at a glance which unit the invoice is for',
      'Bill breakdown now shows a warning when a unit reports negative water usage, prompting you to contact Support before generating or sending invoices',
      'Fixed a bug where the Pay Online URL field on invoice generation could be auto-filled by the browser with an unrelated URL from another website',
      'Fixed a bug where deleting a team member could fail with a database error if that user had received any alert notifications',
    ],
  },
  {
    version: 'v1.11.3',
    date: 'May 6, 2026',
    title: 'Patching the Leaks',
    changes: [
      'Fixed a bug where switching between Daily and Hourly on the usage graph could briefly leave the chart with bars from the previous mode before refreshing',
      'Fixed a bug where the Manage Billing button on the SimpleSUB Service Fee card could be hidden below a payment-failed alert; the card now grows to fit its content',
    ],
  },
  {
    version: 'v1.11.2',
    date: 'May 5, 2026',
    title: 'You Asked, We Listened',
    changes: [
      'New Per Meter Breakdown chart on multi-meter units, with a clickable legend to filter to a single meter',
      'Bill Model can now be changed on existing units, so changing how a unit is billed no longer requires recreating it and re-attaching tenants and meters',
      'New Non Billable option for regular units, useful for subsidized housing or units that aren’t being charged for water; tenants and meters stay attached',
      'Unit-Flat Fees can now be applied to multiple units at once, with a Select all option to quickly cover every billable unit',
      'Fixed a bug where properties on the home page were displayed in an arbitrary order; they now sort alphabetically to match the side navigation',
      'Fixed a bug where the Add Unit and Add Common Area forms kept the previous values after closing; they now reset every time the form is opened',
    ],
  },
  {
    version: 'v1.11.1',
    date: 'April 30, 2026',
    title: 'Patching the Leaks',
    changes: [
      "Fixed a bug where the Tenant Usage Report email only showed the tenant's first name instead of their full name",
      'Fixed a bug where the Tenant Usage Report PDF download used a generic filename; it now uses a name based on the property, unit, and date range',
      'Fixed a bug where the Download Report button on the Tenant Usage Report briefly disappeared when clicked',
      'Fixed a bug where Property, Unit, Common Area, and Organization names could be saved blank; these fields also now show a character counter while typing',
      'Fixed a bug where Property addresses could be saved with a missing Street, City, State, or Zip',
      'Fixed a bug where saving a Property name or Unit name change showed no loading indicator',
      'Fixed a bug where the deletion confirmation message referenced a single tenant even when multiple tenants were selected',
      'Fixed a bug where the Edit Tenant dialog could show error highlights on Unit Name and Email fields when the values were valid',
      'Fixed a bug where Tenants, Tenant Alerts, and Bills tables stayed on a later page after clicking a column header to change sort, instead of returning to the first page',
      'Fixed a bug where minimizing the side navigation showed a stray scrollbar below the property list',
      'Fixed a bug where clicking Contact Support could pop up the account menu after returning to the tab',
      'Fixed a bug where opening the release notes button rapidly required multiple back-arrow presses to leave the page',
      'Fixed a bug where units with identical names appeared merged as a single row in billing breakdowns, with missing details on later pages',
      'Fixed a bug where the loading indicator on the usage chart could extend beyond the chart area',
      'Fixed a bug where the address shown in the Send Usage Reports confirmation had the state and city swapped, and was missing the unit/suite line',
    ],
  },
  {
    version: 'v1.11.0',
    date: 'April 30, 2026',
    title: 'You Asked, We Listened',
    changes: [
      'New hourly water usage view on property and unit charts, with a Daily/Hourly toggle and matching spreadsheet exports. Contact support to enable.',
      "Fixed a bug where spreadsheet exports used the browser's local timezone instead of the property's timezone for timestamps",
      'Fixed a bug where Export to Spreadsheet would download a blank file if clicked before the chart finished loading',
      "Fixed a bug where the usage chart wouldn't show a loading indicator while data was being fetched",
      'Fixed a bug where the Generate Invoices dialog would flip back to the configuration form after the SSW invoice PDFs finished generating, instead of staying on the success view',
    ],
  },
  {
    version: 'v1.10.0',
    date: 'April 23, 2026',
    title: 'You Asked, We Listened',
    changes: [
      'New Alert History tab on all property views. See a full log of leak detections, high flow alerts, meter health events, and device connections with notification delivery details',
      'Usage graph date range now persists when navigating between units and properties, making unit-by-unit comparisons easier',
      'Added search bar to Tenants and Tenant Alerts tables for quick lookup by name, email, or unit',
      'Added "All" option to Tenants and Tenant Alerts table pagination',
      'Updated Leak Alert description to clarify detection behavior: "Notify me if at least 1 gallon is detected every hour for X hours"',
      'Fixed usage CSV export sorting units in wrong order (e.g., Unit 10 before Unit 2)',
    ],
  },
  {
    version: 'v1.9.1',
    date: 'April 10, 2026',
    title: 'Support Chat',
    changes: ['Added live support chatbot for quick help directly within the app'],
  },
  {
    version: 'v1.9.0',
    date: 'April 8, 2026',
    title: 'You Asked, We Listened',
    changes: [
      'New "Import Units" option to bulk-create units from a CSV or Excel spreadsheet',
      'New "Import units from spreadsheet" option during property creation for faster setup',
      'Added property name and address to usage CSV exports for clarity when sharing',
      'New per-unit "Usage Graph on Invoices" toggle: hide the daily usage chart on generated SSW invoice PDFs for individual units (helpful when meter data is incomplete or estimated). Configurable from the unit detail screen, and overridable per batch when generating invoices.',
      'Fixed invoice rate formatting: per-gallon rates now show 4 decimal places, flat fees show standard currency format',
      'Fixed a bug where closing the Generate Invoices dialog while PDFs were still generating would prevent the breakdown from auto-refreshing, requiring a page reload to see the new invoices',
    ],
  },
  {
    version: 'v1.8.1',
    date: 'March 26, 2026',
    title: 'Rent Manager Upload Improvements',
    changes: [
      'Rent Manager upload confirmation now shows tenant name for each unit',
      'Rent Manager units with no active tenant are flagged and automatically skipped during upload',
      'Fixed Rent Manager charge upload failing when a unit has no current tenant',
    ],
  },
  {
    version: 'v1.8.0',
    date: 'March 23, 2026',
    title: 'Rent Manager API Integration',
    changes: [
      'New "Upload Charges" for push billing charges directly to Rent Manager via API, no CSV needed',
      'New "Connect to Rent Manager" setup to automatically pulls properties, units, and charge types from the Rent Manager API for easy mapping',
      'Added upload confirmation dialog showing each unit and charge amount before uploading',
      'New "Send to Rent Manager" button on the bill breakdown with options to export CSV or upload charges',
      'New selection toolbar above the invoice table with Download and Email buttons',
      'PMS Settings card now shows a summary with status badges (CSV Ready, API Ready) when configured',
      'Fixed property usage CSV export showing "Unknown" values for large properties',
      'Fixed a bug causing "Unknown error" when attempting to delete a unit or property with linked devices',
    ],
  },
  {
    version: 'v1.7.0',
    date: 'March 6, 2026',
    title: 'PMS Integration (Rent Manager)',
    changes: [
      'New PMS Export: export billing data as a Rent Manager-compatible CSV directly from the bill breakdown',
      'New PMS Settings card on the property Billing tab to configure Rent Manager connection details',
      'Added per-unit Rent Manager name mapping for accurate charge exports',
    ],
  },
  {
    version: 'v1.6.2',
    date: 'February 23, 2026',
    title: 'Notification Banner Fixes',
    changes: [
      'Fixed payment warning banner overlapping the DELETE button on the property page',
      'Fixed notification banners stacking on top of each other when both are visible',
    ],
  },
  {
    version: 'v1.6.1',
    date: 'February 23, 2026',
    title: 'Invoice Email Tracking',
    changes: [
      'New "Invoice Sent" column in bill details shows when each invoice was emailed, in the property\'s timezone',
      'Added warning when re-sending invoices that have already been emailed',
      'New "Invoices Emailed" column on the Billing tab shows email sent count per bill',
    ],
  },
  {
    version: 'v1.6.0',
    date: 'February 18, 2026',
    title: 'Email Invoices',
    changes: [
      'New ability to email SSW invoices directly to tenants with PDF attachments',
      'Added selective email sending — use checkboxes to choose which tenants receive SSW invoices by email',
      'New confirmation dialog shows a table of Unit, Full Name, and Email before sending',
    ],
  },
  {
    version: 'v1.5.6',
    date: 'February 9, 2026',
    title: 'Invoice Download Naming',
    changes: ['Batch SSW invoice downloads now use descriptive filenames'],
  },
  {
    version: 'v1.5.5',
    date: 'February 5, 2026',
    title: 'Team Role Safety Improvements',
    changes: [
      'Added confirmation dialog when owners downgrade their own role',
      'Prevents the last owner from downgrading themselves with a helpful error message',
    ],
  },
  {
    version: 'v1.5.4',
    date: 'February 4, 2026',
    title: 'Bill Editing Improvements',
    changes: [
      'Added ability to add, edit, and remove fees when editing a bill',
      'Fixed bug where editing a bill would cause invoices to show $0',
    ],
  },

  {
    version: 'v1.5.3',
    date: 'February 4, 2026',
    title: 'Invoice Due Date Improvements',
    changes: [
      'Various minor improvements to invoice due date handling',
      'Due date displays "—" when no invoice exists',
      'Fixed due date not persisting when reopening bill details',
    ],
  },
  {
    version: 'v1.5.2',
    date: 'February 3, 2026',
    title: 'Timezone support for Invoice Dates',
    changes: ['Fixed invoice dates to consistently use property timezone'],
  },
  {
    version: 'v1.5.1',
    date: 'February 2, 2026',
    title: 'Invoice Due Date Settings',
    changes: [
      'Added configurable "Days Until Due" for invoices, with default setting per property',
      'Renamed "Payment Instructions" to "Comments" for clarity and flexibility',
      'Fixed invoice generation failing for properties or units with non-ASCII characters in their names',
    ],
  },
  {
    version: 'v1.4.0',
    date: 'January 22, 2026',
    title: 'SSW Invoices',
    changes: [
      'New SSW invoice generation with usage graphs and detailed line items',
      'Supports custom payment instructions and online payment links, with defaults per property',
      'Bug fixes: Resolved issues with invoice contact assignment, "Unit Name" column in Tenants table now sorts alphanumerically',
    ],
  },
  {
    version: 'v1.3.2',
    date: 'January 6, 2026',
    title: 'Update Link',
    changes: ['Update "Contact Support" link'],
  },
  {
    version: 'v1.3.1',
    date: 'December 9, 2025',
    title: 'Update Link',
    changes: ['Update email delivery troubleshooting link'],
  },
  {
    version: 'v1.3.0',
    date: 'December 8, 2025',
    title: 'Account Roles & Permissions',
    changes: [
      'New role-based access control system with Owner, Admin, and Billing Manager roles for team members',
      'Added user permissions, checking access across the application',
      'Owner role has full access including team management and billing',
      'Admin role has full access, except inviting or editing Owners',
      'Billing Manager role grants access to billing features and payment management. They cannot invite/edit users, view usage data, or modify notification settings for others',
      'Added ability for Owners and Admins to delete team members (Admins cannot delete Owners)',
      'Added "You" badge next to the currently signed-in user in Team Members table',
      "All team members are included in the notifications table (even if they haven't reset password)",
      'Fixed: Resent invites used the description as the invite email memo',
    ],
  },
  {
    version: 'v1.2.3',
    date: 'November 3, 2025',
    title: 'Usage Export Fixes',
    changes: [
      'Fixed: Usage exports now include all selected days and display accurate values with proper formatting',
      'Fixed: Custom date range exports now return the complete selected date range',
    ],
  },
  {
    version: 'v1.2.2',
    date: 'Oct 29, 2025',
    title: 'Multi-Line Item Subscription Support',
    changes: [
      'Added support for displaying multiple line items in SimpleSUB Service Fee subscriptions',
      'New interactive tooltip shows itemized breakdown when hovering over subscription amounts',
    ],
  },
  {
    version: 'v1.2.1',
    date: 'Oct 23, 2025',
    title: 'Service Fee Enhancements',
    changes: [
      'Added support for SimpleSUB Service Fee subscriptions scheduled to start on a future date',
      'Improved warning message clarity for canceled Service Fee subscriptions',
    ],
  },
  {
    version: 'v1.2.0',
    date: 'Oct 21, 2025',
    title: 'SimpleSUB Service Fee Management',
    changes: [
      'New SimpleSUB Service Fee subscription management in Account Settings with payment method setup',
      'Added payment warning banner to prompt users to add payment methods for uninterrupted service',
      'Account lockout protection for payment-related issues with self-service payment portal access',
      'New subscription status dashboard showing active subscriptions, billing dates, and payment details',
      'Organization name now displayed for all account types in Account Settings',
      'Default fee type changed from Split to Flat',
      'Fixed bug where unit and property usage exports included an extra day beyond the selected date range',
    ],
  },
  {
    version: 'v1.1.3',
    date: 'Oct 1, 2025',
    title: 'Usage Export Accuracy Fix',
    changes: [
      'Fixed bug where usage export spreadsheets showed different totals than usage graphs due to double-rounding',
    ],
  },
  {
    version: 'v1.1.2',
    date: 'Sep 8, 2025',
    title: 'Notification Settings Bug Fix',
    changes: [
      'Fixed bug where editing team member details (phone/description) would reset SMS notification preferences',
      'Improved Send Usage Report confirmation dialog styling',
    ],
  },
  {
    version: 'v1.1.1',
    date: 'Sep 5, 2025',
    title: 'UI Improvements & Validation',
    changes: [
      'Added Version History page with detailed changelogs',
      'Fixed duplicate headers and buttons in property creation flow',
      'Added automatic version checking for outdated webapp instances',
      'Provide redirect for invalid property IDs to home',
    ],
  },
  {
    version: 'v1.1.0',
    date: 'Sep 4, 2025',
    title: 'Phone Numbers & Enhanced Billing',
    changes: [
      'Added phone number support for users and tenants',
      'Added alert configuration for users and tenants (device health, leaks, etc.)',
      'Added ability to include fees on bills for everyone (not just Stripe-enabled accounts)',
      'Bills show breakdown of water and fees per unit',
      'Spreadsheet exports include water, fees, and total cost breakdown',
      'Added "Add New Property" button on the home page',
      'Bug fixes: Invoice statuses refresh when selecting View Bill, Unit usage graphs show selected dates, Safari download issues resolved',
    ],
  },
  {
    version: 'v1.0.29',
    date: 'Jul 25, 2025',
    title: 'Outage Handling & User Experience',
    changes: [
      'New Maintenance/Outage page shown if databases are down',
      'Clear red error messages and success notifications for password reset',
      'Bug fixes: Selecting an action from Account menu now closes the menu',
    ],
  },
  {
    version: 'v1.0.28',
    date: 'Jul 21, 2025',
    title: 'Team Management & Settings',
    changes: [
      'New Feature: Invite and resend invites for team members',
      'New tabs layout in settings',
    ],
  },
  {
    version: 'v1.0.27',
    date: 'Jul 21, 2025',
    title: 'Billing Flow Overhaul',
    changes: [
      'Simplified billing hamburger menu. Actions consolidated into "View Bill" popup',
      'New view in bill review screen',
      'Empty Bill and Tenant tables now prompt users to add data',
      'Bug fixes: Fees correctly included in totals, common areas hidden when adding fees, PDF downloads include all units',
    ],
  },
  {
    version: 'v1.0.26',
    date: 'Jun 16, 2025',
    title: 'Download & Billing Improvements',
    changes: ['Fixed mislabeled download buttons', 'Clearer button wording in billing flow'],
  },
  {
    version: 'v1.0.25',
    date: 'Jun 13, 2025',
    title: 'Invoice Contacts & Billing Accuracy',
    changes: [
      'First tenant added to a unit is automatically set as invoice contact',
      'Occupancy dates always visible when adding a tenant',
      'Fixed billing rounding issue',
      'UI cleanup: clearer labels and info icons, "CSV" renamed to "Spreadsheet"',
    ],
  },
  {
    version: 'v1.0.24',
    date: 'Jun 9, 2025',
    title: 'Stability Improvements',
    changes: ['Internal testing improvements for better stability and reliability'],
  },
];
